import React, { useState } from "react";
import { EyeIcon, EyeSlashIcon } from "@heroicons/react/24/solid";

interface IInputProps {
  label: string;
  type: string;
  value?: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  validations?: ((value: string) => string | null)[]; // Array of custom validation functions
  useBuiltInValidations?: boolean; // Enable built-in common validations
  validateOnBlur?: boolean; // Trigger validations on blur
}

const InputBox: React.FC<IInputProps> = ({
  label,
  type,
  value = "",
  onChange,
  required = false,
  disabled = false,
  readOnly = false,
  validations = [],
  useBuiltInValidations = true,
  validateOnBlur = true,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);
  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  // Built-in validations
  const builtInValidations: ((value: string) => string | null)[] = [];
  if (useBuiltInValidations) {
    if (required) {
      builtInValidations.push((value) =>
        value.trim() === "" ? "This field is required" : null
      );
    }

    if (type === "email") {
      builtInValidations.push((value) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
          ? null
          : "Please enter a valid email"
      );
    }

    if (type === "tel") {
      builtInValidations.push((value) =>
        /^\+?[0-9]{7,15}$/.test(value)
          ? null
          : "Please enter a valid phone number"
      );
    }

    if (type === "url") {
      builtInValidations.push((value) =>
        /^(https?:\/\/)?([\w\d-]+\.)+[\w]{2,}(:\d{1,5})?(\/\S*)?$/.test(value)
          ? null
          : "Please enter a valid URL"
      );
    }
  }

  // Validation Logic
  const runValidations = (currentValue: string) => {
    for (const validate of [...builtInValidations, ...validations]) {
      const error = validate(currentValue);
      if (error) {
        setValidationError(error);
        return;
      }
    }
    setValidationError(null);
  };

  const handleBlur = () => {
    setTouched(true);
    runValidations(value);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
    if (!validateOnBlur) {
      runValidations(e.target.value);
    }
  };

  return (
    <div
      className={`relative z-0 w-full flex flex-col items-start justify-center  ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
    >
      <div className="relative w-full flex items-center justify-between">
        <input
          type={type === "password" && !showPassword ? "password" : "text"}
          id="floating_standard"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          className="block py-2.5  w-full font-Poppins tracking-wider font-semibold text-md text-gray-900 bg-transparent border-0 border-b-2 border-gray-400 appearance-none focus:outline-none focus:ring-0 focus:border-blue-600 peer"
          placeholder=" "
          disabled={disabled}
          readOnly={readOnly}
        />

        {/* Label */}
        <label
          htmlFor="floating_standard"
          className={`absolute font-Poppins font-semibold text-md text-sky-900 duration-300 transform scale-75 -translate-y-6 top-3 -z-10 origin-[0] peer-focus:left-0 peer-focus:text-sky-900 ${
            value
              ? "scale-75 -translate-y-6 left-0"
              : "peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 left-0"
          }`}
        >
          {label}
          {required && <span className="text-sm text-red-500"> *</span>}
        </label>

        {/* Clear Icon */}
        {!readOnly && value && (
          <button
            type="button"
            className="absolute right-2 top-0 h-full flex items-center cursor-pointer"
            onClick={() => onChange("")} // Clear the input value
            disabled={disabled}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="currentColor"
              className="w-5 h-5 text-gray-500"
            >
              <path
                fillRule="evenodd"
                d="M5.47 5.47a.75.75 0 0 1 1.06 0L12 10.94l5.47-5.47a.75.75 0 1 1 1.06 1.06L13.06 12l5.47 5.47a.75.75 0 1 1-1.06 1.06L12 13.06l-5.47 5.47a.75.75 0 0 1-1.06-1.06L10.94 12 5.47 6.53a.75.75 0 0 1 0-1.06Z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        )}

        {/* Password Visibility Toggle */}
        {type === "password" && value && (
          <span
            className="absolute right-10 top-0 h-full flex items-center cursor-pointer"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? (
              <EyeSlashIcon className="w-5 h-5 text-gray-500" />
            ) : (
              <EyeIcon className="w-5 h-5 text-gray-500" />
            )}
          </span>
        )}
      </div>

      {/* Validation Error */}
      {touched && validationError && (
        <div className="w-full flex items-center justify-start pt-1">
          <span className="font-semibold text-xs text-red-500">
            {validationError}
          </span>
        </div>
      )}
    </div>
  );
};

export default InputBox;
